import type { CommunicationsDto } from '@workers-benefit-fund/api'
import type { FileAttachments } from 'src/default/interfaces/communication/email-communication'

import { incrementCommunicationsRevisionsOnSuccess } from '../models/communication.store'

import { ENDPOINTS_TAGS, rootApi } from '@v2/store/api/rootApi'

export interface GetCommunicationsByPersonArgs {
  personId: string
  organizationId?: string
  fromDate: string
  toDate: string
  search?: string
  revision: number
  isCurrentWindow: boolean
}

export interface CommunicationsByPersonResponse {
  records: CommunicationsDto[]
  recordsLeft: number
  lastActivityDate?: string
  totalRecords?: number
}

export interface SendEmailArgs {
  personId: string
  organizationId: string
  fromEmailId: string
  replyToEmailId?: string
  taskId?: string
  isDeltaType?: string
  toEmail?: string
  subject: string
  body: string
  attachments: File[]
  attachmentsFilesName: FileAttachments[]
}

const getCommunicationTimestamp = (record: CommunicationsDto) => {
  const timestamp = new Date(record.startedAt ?? record.createdAt).getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}

export const mergeCommunicationsResponses = (
  currentCache: CommunicationsByPersonResponse,
  newData: CommunicationsByPersonResponse,
  replaceCurrentWindow = false,
): CommunicationsByPersonResponse => {
  const recordsById = new Map<string, CommunicationsDto>()

  if (!replaceCurrentWindow) {
    currentCache.records?.forEach((record) =>
      recordsById.set(record.id, record),
    )
  }
  newData.records?.forEach((record) => recordsById.set(record.id, record))

  return {
    ...newData,
    records: Array.from(recordsById.values()).sort(
      (a, b) => getCommunicationTimestamp(b) - getCommunicationTimestamp(a),
    ),
  }
}

export const communicationsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getCommunicationsByPerson: builder.query<
      CommunicationsByPersonResponse,
      GetCommunicationsByPersonArgs
    >({
      query: ({ personId, organizationId, fromDate, toDate, search }) => ({
        url: `/v4/communications/person/${personId}`,
        method: 'GET',
        params: {
          fromDate,
          toDate,
          organizationId,
          search: search || undefined,
        },
      }),
      transformResponse: (response: CommunicationsByPersonResponse) =>
        mergeCommunicationsResponses(
          { records: [], recordsLeft: 0 },
          response,
          true,
        ),
      serializeQueryArgs: ({ queryArgs }) =>
        JSON.stringify({
          personId: queryArgs.personId,
          organizationId: queryArgs.organizationId,
          search: queryArgs.search,
          revision: queryArgs.revision,
        }),
      merge: (currentCache, newData, { arg }) =>
        mergeCommunicationsResponses(
          currentCache,
          newData,
          arg.isCurrentWindow,
        ),
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.fromDate !== previousArg?.fromDate ||
        currentArg?.toDate !== previousArg?.toDate,
      providesTags: (_result, _error, { personId }) => [
        { type: ENDPOINTS_TAGS.COMMUNICATIONS, id: personId },
      ],
      keepUnusedDataFor: 0,
    }),
    sendEmail: builder.mutation<void, SendEmailArgs>({
      query: (data) => {
        const formData = new FormData()
        formData.append('fromEmailId', data.fromEmailId)
        if (data.replyToEmailId) {
          formData.append('replyToEmailId', data.replyToEmailId)
        }
        if (data.taskId) {
          formData.append('taskId', data.taskId)
        }
        if (data.isDeltaType) {
          formData.append('isDeltaType', data.isDeltaType)
        }
        formData.append('personId', data.personId)
        if (data.toEmail) {
          formData.append('toEmail', data.toEmail)
        }
        if (data.attachmentsFilesName) {
          formData.append(
            'attachmentsFilesName',
            JSON.stringify(data.attachmentsFilesName),
          )
        }
        formData.append('subject', data.subject)
        formData.append('body', data.body)
        data.attachments.forEach((attachment) => {
          formData.append('attachments', attachment)
        })

        return {
          url: '/v4/persons/email/',
          method: 'POST',
          data: formData,
        }
      },
      onQueryStarted: async (
        { organizationId, personId },
        { dispatch, queryFulfilled },
      ) => {
        await incrementCommunicationsRevisionsOnSuccess(
          queryFulfilled,
          dispatch,
          [{ organizationId, personId }],
        )
      },
    }),
  }),
})

export const {
  useGetCommunicationsByPersonQuery,
  useSendEmailMutation: useSendEmail,
} = communicationsApi
